import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useStore } from 'react-redux';

import { DocumentFileUpload, DotBadge, UploadedFile, collegeTransform } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { NuggetCardItem } from '@backrs_ui/grow-ui/src/components/NuggetCards/NuggetCardItem';
import { useGetCollegeByInstitutionIdQuery } from '@backrs_ui/grow-ui/src/redux/college/college.api';
import type { RootState } from '@backrs_ui/grow-ui/src/redux/store';
import {
  useExtractAwardLetterMutation,
  useSaveAwardLetterMutation,
  userCollegesApi,
} from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.api';
import {
  AwardAmountField,
  AwardAmounts,
  AwardLetter,
  SCHOLARSHIP_RENEWAL_ANSWERS,
  ScholarshipRenewal,
  UserCollege,
} from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';
import { MAX_UPLOAD_BYTES, uploadFile } from '@backrs_ui/grow-ui/src/utils/uploadFile';

import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';

import {
  LetterChecks,
  amountsOf,
  withExtracted,
  netCostOf,
  percentFromEstimate,
  hasScholarships,
} from './awardLetter.util';
import { useAgentContext } from './AwardLetterPage.agentContext';
import { AmountsModal } from './components/AmountsModal';
import { AwardLetterAskLubav } from './components/AwardLetterAskLubav';
import { NetCostCard } from './components/NetCostCard';

const STRINGS = appStrings.modules.myColleges.awardLetter;
const ACCEPTED_FILE_TYPES = ['.pdf', '.png', '.jpg', '.jpeg'];

type Letter = Readonly<{
  amounts: AwardAmounts;
  renewal: ScholarshipRenewal | null;
  media: AwardLetter['media'];
}>;

const letterOf = (saved: AwardLetter): Letter => ({
  amounts: amountsOf(saved),
  renewal: saved.scholarships_renewable,
  media: saved.media,
});

type AwardLetterPageProps = Readonly<{
  college: UserCollege;
  letter: AwardLetter;
}>;

export const AwardLetterPage = ({ college, letter: savedLetter }: AwardLetterPageProps) => {
  const navigate = useNavigate();
  const { backrs } = useTheme();
  const store = useStore<RootState>();
  const letter = letterOf(savedLetter);
  const [letterChecks, setLetterChecks] = useState<LetterChecks | null>(null);
  const [uploadError, setUploadError] = useState<'upload' | 'read' | null>(null);
  const [editing, setEditing] = useState<AwardAmountField | null>(null);
  const { data: collegeDetails } = useGetCollegeByInstitutionIdQuery(
    String(college.institution_id),
  );
  const [extractAwardLetter] = useExtractAwardLetterMutation();
  const [saveAwardLetter, { isLoading: isSaving, isError: saveFailed, reset: resetSave }] =
    useSaveAwardLetterMutation();

  const { amounts, renewal, media } = letter;
  const netCost = netCostOf(amounts);

  useAgentContext({ college, amounts, renewal, netCost, letterChecks });

  const save = async (next: Letter) => {
    const result = await saveAwardLetter({
      userCollegeId: college.id,
      ...next.amounts,
      scholarships_renewable: next.renewal,
      media: next.media,
    });

    return 'data' in result;
  };

  const handleLetter = async (files: File | File[]) => {
    const file = Array.isArray(files) ? files[0] : files;

    setUploadError(null);
    const uploaded = await uploadFile({ file }).catch(() => null);

    if (!uploaded) {
      setUploadError('upload');

      return;
    }

    const result = await extractAwardLetter({
      userCollegeId: college.id,
      mediaId: uploaded.mediaId,
    });

    if (!('data' in result) || !result.data) {
      setUploadError('read');

      return;
    }

    const current = letterOf(
      userCollegesApi.endpoints.getAwardLetter.select(college.id)(store.getState()).data ??
        savedLetter,
    );
    const saved = await save({
      amounts: withExtracted(current.amounts, result.data),
      renewal: result.data.scholarships_renewable ?? current.renewal,
      media: { id: uploaded.mediaId, name: file.name },
    });

    if (saved) setLetterChecks(result.data);
  };

  const handleEditSave = async (nextAmounts: AwardAmounts) => {
    if (await save({ ...letter, amounts: nextAmounts })) setEditing(null);
  };

  return (
    <Box sx={pageSx}>
      <Box sx={columnsSx}>
        <Box sx={columnSx}>
          <Box sx={uploadSx}>
            <DocumentFileUpload
              inputId="award-letter-upload-input"
              acceptedFileTypes={ACCEPTED_FILE_TYPES}
              maxFileSize={MAX_UPLOAD_BYTES}
              onFileSelect={handleLetter}
              onError={() => setUploadError('upload')}
              dragAndDropText={STRINGS.upload.dragAndDrop}
            />
            {uploadError && (
              <Typography id="award-letter-upload-failed" variant="text-sm-medium" sx={errorSx}>
                {uploadError === 'upload' ? STRINGS.upload.uploadFailed : STRINGS.upload.readFailed}
              </Typography>
            )}
            {media?.name && (
              <UploadedFile
                fileName={media.name}
                fileSize=""
                fileType={media.name.split('.').pop()}
              />
            )}
          </Box>

          {saveFailed && editing === null && (
            <Typography id="award-letter-save-failed" variant="text-sm-medium" sx={errorSx}>
              {STRINGS.saveFailed}
            </Typography>
          )}

          <NetCostCard
            amounts={amounts}
            netCost={netCost}
            percentFromEstimate={percentFromEstimate(netCost, college.estimated_net_cost)}
            fromLetter={Boolean(media)}
            onEdit={(field) => {
              resetSave();
              setEditing(field);
            }}
          />

          {hasScholarships(amounts) && (
            <Box id="award-letter-renewal" sx={renewalCardSx}>
              <Box>
                <DotBadge label={STRINGS.badges.followUp} dotColor={backrs.colors['Brand/500']} />
              </Box>
              <Typography id="award-letter-renewal-question" variant="text-md-regular">
                {STRINGS.renewal.question}
              </Typography>
              <RadioGroup aria-labelledby="award-letter-renewal-question" value={renewal ?? ''}>
                {SCHOLARSHIP_RENEWAL_ANSWERS.map((answer) => (
                  <FormControlLabel
                    key={answer}
                    value={answer}
                    checked={renewal === answer}
                    control={
                      <Radio
                        id={`award-letter-renewal-${answer.replace(/_/g, '-')}`}
                        onChange={() => save({ ...letter, renewal: answer })}
                      />
                    }
                    label={STRINGS.renewal.options[answer]}
                  />
                ))}
              </RadioGroup>
            </Box>
          )}
        </Box>

        <Box sx={columnSx}>
          {collegeDetails && (
            <NuggetCardItem
              nugget={collegeTransform(collegeDetails)}
              onClick={() => navigate(`${APP_ROUTE.UNIVERSITY}/${college.academic_profile_id}`)}
              fluid
            />
          )}
          {media && (
            <AwardLetterAskLubav
              collegeName={college.name}
              hasScholarship={Boolean(amounts.scholarships)}
            />
          )}
        </Box>
      </Box>

      {editing && (
        <AmountsModal
          field={editing}
          amounts={amounts}
          saving={isSaving}
          saveFailed={saveFailed}
          onClose={() => setEditing(null)}
          onSave={handleEditSave}
        />
      )}
    </Box>
  );
};

const pageSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minWidth: 0,
  gap: backrs.spacing['spacing-2xl'],
  paddingBottom: backrs.spacing['spacing-4xl'],
});

const columnsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(240px, 288px)' },
  gap: backrs.spacing['spacing-3xl'],
  alignItems: 'start',
});

const columnSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  minWidth: 0,
});

const uploadSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const renewalCardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  padding: backrs.spacing['spacing-xl'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const errorSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-error-primary'],
});
